import { Link } from "react-router-dom";
import { ArrowRight, Quote, Trophy, Medal, UserRound, Award, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { IMAGES, SEDI, VALUES, ORIGINI, ANECDOTE, PRESIDENZA, MEDAGLIERE, HONORS, STAFF, RICONOSCIMENTI } from "@/data/site";

export default function ChiSiamo() {
  return (
    <div data-testid="chi-siamo-page">
      <PageHero
        image={IMAGES.chiSiamoBg}
        overline="Chi siamo · dal 1948"
        titleLines={["La nostra", "storia"]}
        subtitle="Dal 1948, la continuità degli atleti che resero Bologna capitale della lotta greco-romana. Molto più di una palestra: una comunità che attraversa le generazioni."
      />

      {/* Story */}
      <section className="bg-cream py-20 md:py-32" data-testid="chi-siamo-story">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-2xl">
                <img src={IMAGES.squadraRighi} alt="Squadra di lotta CAB al Liceo Righi - foto storica" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-4 hidden rounded-xl bg-cab px-7 py-6 text-cream shadow-xl md:block">
                <div className="font-display text-4xl font-extrabold">1948</div>
                <div className="text-xs uppercase tracking-[0.2em] text-cream/80">Anno di fondazione</div>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeading
              overline="Introduzione e origini · 1948 → oggi"
              title="La continuità di una grande tradizione"
            />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                {ORIGINI.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {RICONOSCIMENTI.map((r) => {
                  const argento = r.title.toLowerCase().includes("argento");
                  return (
                    <div
                      key={r.title}
                      className="inline-flex items-center gap-3 rounded-xl border border-cab/20 bg-cab/5 px-4 py-3"
                    >
                      <Award className={`h-6 w-6 shrink-0 ${argento ? "text-slate-400" : "text-gold"}`} />
                      <span className="text-[13px] font-semibold leading-tight text-ink">
                        {r.title} {r.org}
                        {r.year ? ` · ${r.year}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Sedi storiche */}
      <section className="bg-white py-20 md:py-32" data-testid="chi-siamo-sedi">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Un viaggio lungo quasi 80 anni"
            title="Le nostre sedi storiche"
            subtitle="Dal centro alla periferia e ritorno: il cammino del CAB nel tessuto urbano di Bologna."
          />
          <div className="mt-14 md:mt-20">
            {SEDI.map((item, i) => (
              <Reveal key={item.place} delay={i * 0.05}>
                <div
                  className={`group grid gap-4 border-t border-ink/10 py-8 md:grid-cols-12 md:items-baseline md:gap-8 ${
                    item.current ? "bg-cab/5" : ""
                  }`}
                >
                  <div className="md:col-span-3">
                    <span className="font-display text-2xl font-extrabold uppercase tracking-tight text-cab md:text-3xl">
                      {item.era}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink md:col-span-4 md:text-2xl">
                    {item.place}
                  </h3>
                  <p className="text-base leading-relaxed text-muted-foreground md:col-span-5">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-ink/10" />
          </div>
        </div>
      </section>

      {/* Aneddoto storico */}
      <section className="relative overflow-hidden bg-cab-deep py-20 md:py-28" data-testid="chi-siamo-aneddoto">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1000px] px-5 text-center md:px-8">
          <Reveal>
            <Quote className="mx-auto h-10 w-10 text-cab-light" />
          </Reveal>
          <Reveal delay={0.05}>
            <span className="mt-6 block font-display text-xs font-bold uppercase tracking-[0.3em] text-cab-light">
              {ANECDOTE.eyebrow}
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-cream md:text-3xl">
              {ANECDOTE.title}
            </h3>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-cream/80">
              {ANECDOTE.text}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-ink/10 bg-cream py-6">
        <Marquee items={["Comunità", "Inclusione", "Tradizione", "Eccellenza"]} outline />
      </div>

      {/* Manifesto / Values */}
      <section className="bg-cream py-20 md:py-32" data-testid="chi-siamo-valori">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Il nostro manifesto"
            title="Quattro valori, una sola squadra"
            subtitle="Sono i principi che guidano ogni allenamento e ogni gara, dentro e fuori dal tatami."
          />
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 md:mt-20 md:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.06}>
                <div className="h-full bg-cream p-8 md:p-12">
                  <span className="font-display text-6xl font-extrabold tracking-tighter text-cab/25">
                    {v.n}
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Tecnico */}
      <section className="bg-cream py-20 md:py-32" data-testid="chi-siamo-staff">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Staff tecnico"
            title="Gli istruttori del CAB"
            subtitle="Tecnici qualificati FIJLKAM, FIPE e AICS che ogni giorno guidano atleti e appassionati, con competenza e passione."
          />
          <div className="mt-14 space-y-12 md:mt-20">
            {STAFF.map((group) => (
              <div key={group.settore}>
                <Reveal>
                  <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-cab">
                    {group.settore}
                  </h3>
                </Reveal>
                <Stagger className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {group.members.map((m) => (
                    <StaggerItem key={m.name}>
                      <div className="flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-7">
                        <div className="flex items-center gap-4">
                          <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-cab text-cream">
                            <UserRound className="h-7 w-7" />
                          </span>
                          <div>
                            <h4 className="font-display text-lg font-extrabold tracking-tight text-ink">
                              {m.name}
                            </h4>
                            <p className="text-sm text-cab">{m.role}</p>
                          </div>
                        </div>
                        {m.quals.length > 0 && (
                          <ul className="mt-5 space-y-2 border-t border-ink/10 pt-5">
                            {m.quals.map((qual, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground"
                              >
                                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cab/60" />
                                <span>{qual}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Medagliere */}
      <section className="bg-white py-20 md:py-32" data-testid="chi-siamo-medagliere">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Palmarès · dal 1948"
            title="Il medagliere del CAB"
            subtitle={MEDAGLIERE.intro}
          />

          <Stagger className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 md:mt-16 md:grid-cols-4">
            {HONORS.map((h) => (
              <StaggerItem key={h.label} className="bg-white">
                <div className="p-6 md:p-8">
                  <div className="font-display text-4xl font-extrabold tracking-tight text-cab md:text-5xl">
                    {h.value}
                  </div>
                  <div className="mt-2 text-sm font-medium text-muted-foreground">{h.label}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Stagger className="mt-8 grid gap-6 md:grid-cols-2">
            {MEDAGLIERE.disciplines.map((d) => (
              <StaggerItem key={d.name}>
                <div className="h-full rounded-2xl border border-ink/10 bg-cream p-7 md:p-8">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                      <Medal className="h-5 w-5" />
                    </span>
                    <div>
                      <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-cab">
                        {d.tag}
                      </span>
                      <h3 className="font-display text-lg font-extrabold tracking-tight text-ink md:text-xl">
                        {d.name}
                      </h3>
                    </div>
                  </div>
                  <ul className="mt-5 space-y-3">
                    {d.champions.map((c, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-[15px] leading-relaxed text-muted-foreground"
                      >
                        <Trophy className="mt-1 h-4 w-4 shrink-0 text-gold" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Link al medagliere completo */}
      <section className="bg-white pb-20 md:pb-28" data-testid="chi-siamo-medagliere-cta">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <div className="flex flex-col items-start gap-5 rounded-2xl border border-cab/20 bg-cab/5 p-8 md:flex-row md:items-center md:justify-between md:p-10">
              <p className="font-display text-xl font-bold tracking-tight text-ink md:text-2xl">
                Scopri tutti i nostri campioni e le medaglie, disciplina per disciplina.
              </p>
              <Link
                to="/medagliere"
                data-testid="chi-siamo-medagliere-link"
                className="group inline-flex shrink-0 items-center gap-2 rounded-md bg-cab px-7 py-3.5 font-bold text-white transition-transform duration-300 hover:-translate-y-0.5"
              >
                Medagliere completo
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Presidenza + CTA */}
      <section className="bg-cream py-20 md:py-28" data-testid="chi-siamo-presidenza">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full items-center gap-5 rounded-2xl border border-ink/10 bg-white p-8">
                <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-cab text-cream">
                  <UserRound className="h-8 w-8" />
                </span>
                <div>
                  <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cab">
                    {PRESIDENZA.role}
                  </span>
                  <h3 className="font-display text-2xl font-extrabold tracking-tight text-ink">
                    {PRESIDENZA.president}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                    {PRESIDENZA.note}
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col items-start justify-center gap-4 rounded-2xl bg-cab-deep p-8 text-cream">
                <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">
                  Vuoi far parte della squadra?
                </h3>
                <p className="text-cream/80">
                  Una palestra aperta a tutti, dai giovani alla terza età. Prenota la tua prova gratuita.
                </p>
                <Link
                  to="/dove-siamo"
                  data-testid="chi-siamo-cta"
                  className="group mt-2 inline-flex items-center gap-2 rounded-md bg-cream px-7 py-3.5 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Contattaci
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
